import { useEffect, useState } from 'react'

/**
 * The sidebar's collapse and mobile-drawer state, from the prototype's
 * nav-toggle IIFE.
 *
 * Shared by the application's layout and the platform portal's, because the
 * two draw the same sidebar from the same stylesheet and a person who collapses
 * it in one has said what they want in the other. The portal is otherwise a
 * separate application - separate session, separate principal - and this is the
 * one piece of chrome worth having one copy of rather than two that drift.
 */

const MOBILE_QUERY = '(max-width: 900px)'
const STORAGE_KEY = 'ix.nav.collapsed'

export function useNavState() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches)
  const [open, setOpen] = useState(false) // mobile drawer
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      if (!e.matches) setOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    const body = document.body
    body.classList.toggle('nav-open', isMobile && open)
    body.classList.toggle('nav-collapsed', !isMobile && collapsed)
    return () => {
      body.classList.remove('nav-open', 'nav-collapsed')
    }
  }, [isMobile, open, collapsed])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode — ignore */
    }
  }, [collapsed])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  const toggle = () => (isMobile ? setOpen((v) => !v) : setCollapsed((v) => !v))
  const close = () => (isMobile ? setOpen(false) : setCollapsed(true))

  const expanded = isMobile ? open : !collapsed
  return { isMobile, expanded, toggle, close }
}
